<script lang="ts">
  import Board from '~/board/Board.svelte';
  import GameContext from '~/store/GameContext.svelte';
  import DimensionsContext from '~/utils/DimensionsContext.svelte';
  import Gauge from '~/gauge/daily/Gauge.svelte';
  import Word from '~/components/Word.svelte';
  import { Tutorial } from '~/store/daily.svelte';
  import { onboarded } from '~/tutorial/state.svelte';
  import HomeIcon from '~icons/mdi/home';
  import FastForwardIcon from '~icons/mdi/fast-forward';
  import PlayIcon from '~icons/mdi/play';
  import {
    crossing,
    growAnimation,
    sendWordAnimation,
    scoreWordAnimation,
  } from '~/animations/crossfade.svelte';
  import { animationSpeed, delay } from '~/animations/utils.svelte';
  import { tick } from 'svelte';
  import type { Coord, Match } from '~/types';
  import { lettersToBoard, wordToString } from '~/board/utils';
  import ActionButton from '~/components/ActionButton.svelte';
  import { fly } from 'svelte/transition';

  // board[col][row]
  // Step 0: swap [0,1]↔[0,2] (adjacent, col 0) → P-L-A-Y (PLAY)
  // Step 1: swap [0,0]↔[2,0] (non-adjacent, skip col 1) → A-G-O (AGO, 3-col row)
  // Step 2: swap [1,1]↔[1,2] (adjacent, col 1) → B-E-T (BET, 3×3 board)
  // Step 3: auto-clear [0,1]↔[1,1] (adjacent) → rows GO/WE
  // Step 4: auto-clear [0, 0] I
  // Step 5: Z left over
  const board = lettersToBoard([
    ['P', 'A', 'L', 'Y'],
    ['O', 'G', 'W', 'I'], // row 0 is 'O' (middle of OGA)
    ['G', 'B', 'T', 'E'], // row 0 is 'G'
    ['A', 'O', 'E', 'Z'], // row 0 is 'A' (end of OGA)
  ], 'daily');
  board.forEach((col) => col.forEach((tile) => { tile.points = 0; }));

  const tutorialGame = new Tutorial(board);
  const TUTORIAL_ASPECT = 4 / 4;
  let boardAreaWidth = $state(0);
  let boardAreaHeight = $state(0);
  const tutorialDims = $state({
    board: { width: 0, height: 0 },
    aspect: TUTORIAL_ASPECT,
  });
  $effect(() => {
    const aw = boardAreaWidth;
    const ah = boardAreaHeight;
    if (!aw || !ah) return;
    if (aw / ah > TUTORIAL_ASPECT) {
      tutorialDims.board.height = ah;
      tutorialDims.board.width = ah * TUTORIAL_ASPECT;
    } else {
      tutorialDims.board.width = aw;
      tutorialDims.board.height = aw / TUTORIAL_ASPECT;
    }
  });

  type Step = {
    hint: string;
    allowedSwap?: [Coord, Coord];
    words: {
      word: Coord[];
      axis: 'row' | 'col';
    }[];
  };
  const STEPS: Step[] = [
    {
      hint: 'Swap <strong>L</strong> and <strong>A</strong> to spell a word down the column.',
      allowedSwap: [
        [0, 1],
        [0, 2],
      ],
      words: [
        {
          word: [
            [0, 0],
            [0, 1],
            [0, 2],
            [0, 3],
          ],
          axis: 'col',
        },
      ],
    },
    {
      hint: "Tiles don't need to be adjacent — swap <strong>O</strong> and <strong>A</strong> across the row.",
      allowedSwap: [
        [0, 0],
        [2, 0],
      ],
      words: [
        {
          word: [
            [0, 0],
            [1, 0],
            [2, 0],
          ],
          axis: 'row',
        },
      ],
    },
    {
      hint: 'Now swap <strong>T</strong> and <strong>E</strong>. See how sometimes you can trigger chain reactions!',
      allowedSwap: [
        [1, 1],
        [1, 2],
      ],
      words: [
        {
          word: [
            [1, 0],
            [1, 1],
            [1, 2],
          ],
          axis: 'col',
        },
        {
          word: [
            [0, 0],
            [1, 0],
          ],
          axis: 'row',
        },
        {
          word: [
            [0, 0],
            [1, 0],
          ],
          axis: 'row',
        },
        {
          word: [[0, 0]],
          axis: 'row',
        },
      ],
    },
    {
      hint: `Leftover tiles are deducted from your final score,
        try to plan ahead so you're not stuck with hard letters at the end!`,
      words: [],
    },
  ];

  let step = $state(0);
  // svelte-ignore state_referenced_locally
  let allowedSwap = $state<[Coord, Coord] | undefined>(STEPS[step].allowedSwap);

  const handleScore = async (): Promise<boolean> => {
    const s = STEPS[step];
    allowedSwap = undefined;
    for (const { word, axis } of s.words) {
      const w = word.map(([i, j]) => tutorialGame.board[i][j]);

      for (const tile of w) {
        tutorialGame.highlighted[tile.id] =
          axis === 'col' ? 'daily-vertical' : 'daily-horizontal';
      }
      await tick();

      const match = {
        word: w,
        coords: word,
        intersectingIds: [],
      } as unknown as Match;

      tutorialGame.latestWord = match;
      tutorialGame.words++;
      await crossing.launch(match, {}, () => tutorialGame.removeLetters(match));
      await growAnimation.settled();
      await sendWordAnimation.settled();

      await scoreWordAnimation.settled();
      tutorialGame.collapseBoard();
      await delay(animationSpeed.ms / 2);
    }

    step++;
    tutorialGame.disabled = false;
    allowedSwap = STEPS[step].allowedSwap;

    if (tutorialGame.board.flat().filter((t) => t).length === 0) {
      await tick();
    }

    return false;
  };

  const handleEndTurn = async () => {
    tutorialGame.disabled = false;
    return false;
  };

  $effect(() => {
    if (step === 3) {
      const tile = tutorialGame.board[0][0];
      if (tile?.flipped) return;
      tile.flipped = true;
      tick().then(() => {
        tutorialGame.swaps--;
        tutorialGame.board[0][0] = {
          id: tile.id,
          flipped: true,
          flips: tile.flips,
          letter: tile.letter,
          points: -1,
        };
      });
    }
  });

  const handleSkip = () => {
    onboarded.current = true;
  };
</script>

<div class="onboarding">
  <p class="prompt">
    {#key step}
      <span
        in:fly={{ y: 30, duration: 300, delay: 180 }}
        out:fly={{ y: -30, duration: 180 }}
      >
        {@html STEPS[step]?.hint ?? ''}
      </span>
    {/key}
  </p>
  <DimensionsContext dims={tutorialDims as any}>
    <GameContext game={tutorialGame}>
      <Gauge />
      <div
        class="board-area"
        bind:clientWidth={boardAreaWidth}
        bind:clientHeight={boardAreaHeight}
      >
        <Board
          bind:disabled={tutorialGame.disabled}
          {allowedSwap}
          {handleScore}
          {handleEndTurn}
        />
      </div>
    <div class="word-display">
      {#if tutorialGame.latestWord}
        {#key wordToString(tutorialGame.latestWord)}
          <Word
            word={tutorialGame.latestWord.word}
            highlighted={tutorialGame.latestWord.axis === 'col'
              ? 'daily-vertical'
              : 'daily-horizontal'}
          />
        {/key}
      {/if}
    </div>
    </GameContext>
  </DimensionsContext>
  <div class="button-row">
    <ActionButton href="/" outlink={false}><HomeIcon />Home</ActionButton>
    <ActionButton onClick={handleSkip}>
      {#if step === STEPS.length - 1}
        Play
        <PlayIcon />
      {:else}
        Skip tutorial
        <FastForwardIcon />
      {/if}
    </ActionButton>
  </div>
</div>

<style lang="scss">
  @use '~/theme/themify' as *;

  .onboarding {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    gap: 0.5rem;
  }

  .prompt {
    position: relative;
    margin-top: 0;
    height: 9ex;
    max-width: 30em;
    width: 100%;
    text-align: center;
    font-size: 1rem;
    overflow: hidden;

    span {
      position: absolute;
      left: 0;
      right: 0;
    }
  }

  .board-area {
    flex: 1;
    width: 100%;
    min-height: 0;
  }

  .word-display {
    position: relative;
    width: 100%;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .button-row {
    margin-top: 1rem;
    display: flex;
    gap: 0.5rem;
  }
</style>
